import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { authFetch } from '../../utils/auth-fetch';
import { toast } from '../../components/ui/use-toast';
import {
  Globe,
  CheckCircle,
  XCircle,
  Clock,
  Search,
  RefreshCw,
  Plus,
  Filter,
  BookOpen,
  Newspaper,
  GraduationCap,
  TrendingUp,
  ShoppingCart,
  MessageSquare,
  Building,
  FileText,
  HelpCircle,
  CheckSquare,
  AlertTriangle
} from 'lucide-react';

interface SourceDomain {
  id: string;
  domain: string;
  sector_id: number | null;
  sector_name: string | null;
  source_type: string;
  quality_tier: string;
  citation_worthy: boolean;
  is_competitor: boolean;
  is_blocked: boolean;
  domain_name: string;
  description: string | null;
  example_url: string | null;
  status: string;
  proposed_by: string;
  llm_reasoning: string | null;
  reviewed_by: string | null;
  reviewer_email: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  created_at: string;
  updated_at: string;
}

interface Stats {
  total: number;
  byStatus: {
    pending: number;
    approved: number;
    rejected: number;
  };
  byType: Record<string, number>;
}

interface Sector {
  id: number;
  name: string;
}

const sourceTypeIcons: Record<string, React.ReactNode> = {
  news: <Newspaper className="w-4 h-4" />,
  magazine: <BookOpen className="w-4 h-4" />,
  academic: <GraduationCap className="w-4 h-4" />,
  market_research: <TrendingUp className="w-4 h-4" />,
  ecommerce: <ShoppingCart className="w-4 h-4" />,
  blog: <FileText className="w-4 h-4" />,
  social: <MessageSquare className="w-4 h-4" />,
  corporate: <Building className="w-4 h-4" />,
  government: <Building className="w-4 h-4" />,
  patent: <FileText className="w-4 h-4" />,
  unknown: <HelpCircle className="w-4 h-4" />
};

const sourceTypeLabels: Record<string, string> = {
  news: 'News',
  magazine: 'Magazine',
  academic: 'Academic',
  market_research: 'Market Research',
  ecommerce: 'E-commerce',
  blog: 'Blog',
  social: 'Social/UGC',
  corporate: 'Corporate',
  government: 'Government',
  patent: 'Patent',
  unknown: 'Unknown'
};

export default function SourceDomainsManagement() {
  const [domains, setDomains] = useState<SourceDomain[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sectorFilter, setSectorFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [selectedDomains, setSelectedDomains] = useState<Set<string>>(new Set());

  // Dialog states
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [currentDomain, setCurrentDomain] = useState<SourceDomain | null>(null);

  // Review form state
  const [reviewForm, setReviewForm] = useState({
    sourceType: '',
    qualityTier: 'medium',
    citationWorthy: false,
    reviewNotes: ''
  });

  // Add form state
  const [addForm, setAddForm] = useState({
    domain: '',
    sectorId: '',
    sourceType: 'news',
    qualityTier: 'medium',
    citationWorthy: false,
    domainName: '',
    description: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (sectorFilter !== 'all') params.append('sectorId', sectorFilter);
      if (typeFilter !== 'all') params.append('sourceType', typeFilter);
      if (searchTerm) params.append('search', searchTerm);
      params.append('limit', '100');

      const [domainsRes, statsRes, sectorsRes] = await Promise.all([
        authFetch(`/source-domains?${params}`),
        authFetch('/source-domains/stats'),
        authFetch('/sectors')
      ]);

      if (domainsRes.ok) {
        const data = await domainsRes.json();
        setDomains(data.domains || []);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data.stats);
      }

      if (sectorsRes.ok) {
        const data = await sectorsRes.json();
        setSectors(data.sectors || data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load source domains',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sectorFilter, typeFilter, searchTerm]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApprove = async (domain: SourceDomain, updates?: Partial<typeof reviewForm>) => {
    try {
      const res = await authFetch(`/source-domains/${domain.id}/approve`, {
        method: 'POST',
        body: JSON.stringify(updates || {})
      });

      if (res.ok) {
        toast({ title: 'Success', description: `Approved ${domain.domain}` });
        fetchData();
        setReviewDialogOpen(false);
      } else {
        throw new Error('Failed to approve');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to approve classification',
        variant: 'destructive'
      });
    }
  };

  const handleReject = async (domain: SourceDomain, reason?: string) => {
    try {
      const res = await authFetch(`/source-domains/${domain.id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason })
      });

      if (res.ok) {
        toast({ title: 'Rejected', description: `Rejected ${domain.domain}` });
        fetchData();
        setReviewDialogOpen(false);
      } else {
        throw new Error('Failed to reject');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to reject classification',
        variant: 'destructive'
      });
    }
  };

  const handleBulkApprove = async () => {
    if (selectedDomains.size === 0) return;

    try {
      const res = await authFetch('/source-domains/bulk-approve', {
        method: 'POST',
        body: JSON.stringify({ ids: Array.from(selectedDomains) })
      });

      if (res.ok) {
        const data = await res.json();
        toast({
          title: 'Bulk Approved',
          description: `Approved ${data.approved} of ${data.total} domains`
        });
        setSelectedDomains(new Set());
        fetchData();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to bulk approve',
        variant: 'destructive'
      });
    }
  };

  const handleAddDomain = async () => {
    try {
      const res = await authFetch('/source-domains', {
        method: 'POST',
        body: JSON.stringify({
          domain: addForm.domain,
          sectorId: addForm.sectorId === '' ? null : parseInt(addForm.sectorId),
          sourceType: addForm.sourceType,
          qualityTier: addForm.qualityTier,
          citationWorthy: addForm.citationWorthy,
          domainName: addForm.domainName || addForm.domain,
          description: addForm.description || null
        })
      });

      if (res.ok) {
        toast({ title: 'Success', description: `Added ${addForm.domain}` });
        setAddDialogOpen(false);
        setAddForm({
          domain: '',
          sectorId: '',
          sourceType: 'news',
          qualityTier: 'medium',
          citationWorthy: false,
          domainName: '',
          description: ''
        });
        fetchData();
      } else {
        const error = await res.json();
        throw new Error(error.error || 'Failed to add');
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add domain',
        variant: 'destructive'
      });
    }
  };

  const openReviewDialog = (domain: SourceDomain) => {
    setCurrentDomain(domain);
    setReviewForm({
      sourceType: domain.source_type,
      qualityTier: domain.quality_tier,
      citationWorthy: domain.citation_worthy,
      reviewNotes: ''
    });
    setReviewDialogOpen(true);
  };

  const toggleSelectAll = () => {
    const pendingDomains = domains.filter(d => d.status === 'pending');
    if (selectedDomains.size === pendingDomains.length) {
      setSelectedDomains(new Set());
    } else {
      setSelectedDomains(new Set(pendingDomains.map(d => d.id)));
    }
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedDomains);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedDomains(newSelected);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getQualityBadge = (tier: string) => {
    switch (tier) {
      case 'high':
        return <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">High</Badge>;
      case 'medium':
        return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">Medium</Badge>;
      case 'low':
        return <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400">Low</Badge>;
      default:
        return <Badge variant="outline">{tier}</Badge>;
    }
  };

  const pendingCount = stats?.byStatus?.pending || 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Source Domain Classifications</h1>
          <p className="text-muted-foreground">
            Review and manage domain classifications for research sources
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setAddDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Domain
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Domains</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Globe className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className={pendingCount > 0 ? 'ring-2 ring-yellow-500' : ''}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending Review</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.byStatus.pending}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Approved</p>
                  <p className="text-2xl font-bold text-green-600">{stats.byStatus.approved}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Rejected</p>
                  <p className="text-2xl font-bold text-red-600">{stats.byStatus.rejected}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Type Distribution */}
      {stats?.byType && Object.keys(stats.byType).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Source Type Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {Object.entries(stats.byType).map(([type, count]) => (
                <div
                  key={type}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted"
                >
                  {sourceTypeIcons[type] || <Globe className="w-4 h-4" />}
                  <span className="text-sm font-medium">{sourceTypeLabels[type] || type}</span>
                  <Badge variant="secondary">{count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label>Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search domains..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-[150px]">
              <Label>Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-[180px]">
              <Label>Sector</Label>
              <Select value={sectorFilter} onValueChange={setSectorFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sectors</SelectItem>
                  {sectors.map((sector) => (
                    <SelectItem key={sector.id} value={sector.id.toString()}>
                      {sector.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[180px]">
              <Label>Source Type</Label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {Object.entries(sourceTypeLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedDomains.size > 0 && (
              <Button onClick={handleBulkApprove} className="bg-green-600 hover:bg-green-700">
                <CheckSquare className="w-4 h-4 mr-2" />
                Approve Selected ({selectedDomains.size})
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Domains Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">
                  <Checkbox
                    checked={selectedDomains.size > 0 && selectedDomains.size === domains.filter(d => d.status === 'pending').length}
                    onCheckedChange={toggleSelectAll}
                  />
                </TableHead>
                <TableHead>Domain</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Quality</TableHead>
                <TableHead>Citation</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Proposed By</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading...
                  </TableCell>
                </TableRow>
              ) : domains.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No domains found
                  </TableCell>
                </TableRow>
              ) : (
                domains.map((domain) => (
                  <TableRow key={domain.id} className={domain.status === 'pending' ? 'bg-yellow-50/50 dark:bg-yellow-900/10' : ''}>
                    <TableCell>
                      {domain.status === 'pending' && (
                        <Checkbox
                          checked={selectedDomains.has(domain.id)}
                          onCheckedChange={() => toggleSelect(domain.id)}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{domain.domain}</div>
                        {domain.domain_name && domain.domain_name !== domain.domain && (
                          <div className="text-sm text-muted-foreground">{domain.domain_name}</div>
                        )}
                        {domain.sector_name && (
                          <Badge variant="outline" className="mt-1 text-xs">{domain.sector_name}</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {sourceTypeIcons[domain.source_type] || <Globe className="w-4 h-4" />}
                        <span>{sourceTypeLabels[domain.source_type] || domain.source_type}</span>
                      </div>
                    </TableCell>
                    <TableCell>{getQualityBadge(domain.quality_tier)}</TableCell>
                    <TableCell>
                      {domain.citation_worthy ? (
                        <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400">
                          <BookOpen className="w-3 h-3 mr-1" />Citable
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">No</span>
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(domain.status)}</TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground capitalize">{domain.proposed_by}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      {domain.status === 'pending' ? (
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openReviewDialog(domain)}
                          >
                            Review
                          </Button>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleApprove(domain)}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleReject(domain)}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openReviewDialog(domain)}
                        >
                          View
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Review Domain Classification</DialogTitle>
            <DialogDescription>
              Review and optionally modify the classification for {currentDomain?.domain}
            </DialogDescription>
          </DialogHeader>
          {currentDomain && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-muted">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Domain</Label>
                    <p className="font-medium">{currentDomain.domain}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Proposed By</Label>
                    <p className="font-medium capitalize">{currentDomain.proposed_by}</p>
                  </div>
                </div>
                {currentDomain.llm_reasoning && (
                  <div className="mt-4">
                    <Label className="text-muted-foreground">LLM Reasoning</Label>
                    <p className="text-sm mt-1 p-2 bg-background rounded border">
                      {currentDomain.llm_reasoning}
                    </p>
                  </div>
                )}
                {currentDomain.example_url && (
                  <div className="mt-4">
                    <Label className="text-muted-foreground">Example URL</Label>
                    <a
                      href={currentDomain.example_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline block truncate"
                    >
                      {currentDomain.example_url}
                    </a>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Source Type</Label>
                  <Select
                    value={reviewForm.sourceType}
                    onValueChange={(v) => setReviewForm({ ...reviewForm, sourceType: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(sourceTypeLabels).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Quality Tier</Label>
                  <Select
                    value={reviewForm.qualityTier}
                    onValueChange={(v) => setReviewForm({ ...reviewForm, qualityTier: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="citationWorthy"
                  checked={reviewForm.citationWorthy}
                  onCheckedChange={(checked) =>
                    setReviewForm({ ...reviewForm, citationWorthy: checked as boolean })
                  }
                />
                <Label htmlFor="citationWorthy">Citation-worthy (suitable for Q&A references)</Label>
              </div>

              <div>
                <Label>Review Notes (optional)</Label>
                <Textarea
                  placeholder="Add any notes about this classification..."
                  value={reviewForm.reviewNotes}
                  onChange={(e) => setReviewForm({ ...reviewForm, reviewNotes: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
              Cancel
            </Button>
            {currentDomain?.status === 'pending' && (
              <>
                <Button
                  variant="destructive"
                  onClick={() => handleReject(currentDomain, reviewForm.reviewNotes)}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => handleApprove(currentDomain, reviewForm)}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Domain Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Domain Classification</DialogTitle>
            <DialogDescription>
              Manually add a domain classification
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Domain *</Label>
              <Input
                placeholder="example.com"
                value={addForm.domain}
                onChange={(e) => setAddForm({ ...addForm, domain: e.target.value })}
              />
            </div>
            <div>
              <Label>Display Name</Label>
              <Input
                placeholder="Example News"
                value={addForm.domainName}
                onChange={(e) => setAddForm({ ...addForm, domainName: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Source Type *</Label>
                <Select
                  value={addForm.sourceType}
                  onValueChange={(v) => setAddForm({ ...addForm, sourceType: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(sourceTypeLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Quality Tier</Label>
                <Select
                  value={addForm.qualityTier}
                  onValueChange={(v) => setAddForm({ ...addForm, qualityTier: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Sector (optional)</Label>
              <Select
                value={addForm.sectorId}
                onValueChange={(v) => setAddForm({ ...addForm, sectorId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Global (all sectors)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Global (all sectors)</SelectItem>
                  {sectors.map((sector) => (
                    <SelectItem key={sector.id} value={sector.id.toString()}>
                      {sector.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="addCitationWorthy"
                checked={addForm.citationWorthy}
                onCheckedChange={(checked) =>
                  setAddForm({ ...addForm, citationWorthy: checked as boolean })
                }
              />
              <Label htmlFor="addCitationWorthy">Citation-worthy</Label>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                placeholder="What kind of content does this source provide?"
                value={addForm.description}
                onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddDomain} disabled={!addForm.domain}>
              <Plus className="w-4 h-4 mr-2" />
              Add Domain
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
